import type { LocalPhoto } from "./types";

export const MAX_PHOTOS = 3;
export const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const acceptedTypes = ["image/jpeg", "image/png", "image/webp"];

export function validateImage(file: File): void {
  if (
    !acceptedTypes.includes(file.type) ||
    !/\.(jpe?g|png|webp)$/i.test(file.name)
  ) {
    throw new Error("Choose a JPG, PNG, or WebP photo.");
  }
  if (!file.size) throw new Error("This photo is empty. Choose another file.");
  if (file.size > MAX_IMAGE_BYTES)
    throw new Error("Each original photo must be 5 MB or smaller.");
}

export async function imageFingerprint(file: File): Promise<string> {
  const bytes = await file.arrayBuffer();
  if (globalThis.crypto?.subtle) {
    const digest = await crypto.subtle.digest("SHA-256", bytes);
    return Array.from(new Uint8Array(digest), (byte) =>
      byte.toString(16).padStart(2, "0"),
    ).join("");
  }
  // Duplicate detection is a local convenience, not a security check. Support
  // staging HTTP origins where SubtleCrypto is unavailable, without a polyfill.
  let first = 2166136261;
  let second = 5381;
  const view = new Uint8Array(bytes);
  for (let index = 0; index < view.length; index++) {
    const byte = view[index];
    first = Math.imul(first ^ byte, 16777619);
    second = Math.imul(second, 33) ^ byte;
  }
  return `${bytes.byteLength}:${first >>> 0}:${second >>> 0}`;
}

async function decodeImage(file: File): Promise<{
  source: CanvasImageSource;
  width: number;
  height: number;
  close: () => void;
}> {
  // Modern browsers apply EXIF orientation once, before resizing.
  if (typeof createImageBitmap === "function") {
    let bitmap: ImageBitmap;
    try {
      bitmap = await createImageBitmap(file);
    } catch {
      throw new Error("This photo couldn't be opened. Try another image.");
    }
    return {
      source: bitmap,
      width: bitmap.width,
      height: bitmap.height,
      close: () => bitmap.close(),
    };
  }
  const url = URL.createObjectURL(file);
  try {
    const image = new Image();
    image.src = url;
    await image.decode();
    return {
      source: image,
      width: image.naturalWidth,
      height: image.naturalHeight,
      close: () => URL.revokeObjectURL(url),
    };
  } catch {
    URL.revokeObjectURL(url);
    throw new Error("This photo couldn't be opened. Try another image.");
  }
}

export async function processImage(
  file: File,
  fingerprint: string,
): Promise<LocalPhoto> {
  validateImage(file);
  const decoded = await decodeImage(file);
  try {
    if (
      !decoded.width ||
      !decoded.height ||
      decoded.width * decoded.height > 80_000_000
    ) {
      throw new Error(
        "This photo is too large to process. Choose a smaller image.",
      );
    }
    const scale = Math.min(1, 1800 / Math.max(decoded.width, decoded.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(decoded.width * scale));
    canvas.height = Math.max(1, Math.round(decoded.height * scale));
    const context = canvas.getContext("2d");
    if (!context) throw new Error("Your browser couldn't process this photo.");
    context.drawImage(decoded.source, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (result) =>
          result
            ? resolve(result)
            : reject(new Error("Photo processing failed. Try another image.")),
        "image/webp",
        0.85,
      );
    });
    return {
      id: fingerprint,
      fingerprint,
      name: file.name,
      blob,
      previewUrl: URL.createObjectURL(blob),
      width: canvas.width,
      height: canvas.height,
    };
  } finally {
    decoded.close();
  }
}
